import React from "react";
import { PreventiveCards } from "@/shared/components/PreventiveCards";
// test start ENG-18988 prevCareCompare
import { PreventiveCompareTable, isPrevCareCompareVariant } from "@/shared/components/PreventiveCompareTable";
// test end
import PreventiveCareHeader from "../PreventiveCareHeader";
import { PreventiveConfig, PreventiveOption } from "@/shared/utils/CoverageUtils";
import { Pet } from "@/shared/types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { cn } from "@/shared/utils";
import { IconUtils } from "@/shared/utils/IconUtils";

interface PreventiveCareProps {
    activePet?: Pet;
    preventiveOptions?: PreventiveOption[];
    preventiveConfig: PreventiveConfig;
    underwriter?: PetUnderwriterType;
    wellnessValue: "wellness-gold" | "wellness-platinum" | "None";
    handleChange: (value: string, petId?: string) => void;
    showNumbered?: boolean;
    showPlatinumRecommendation?: boolean;
    showClickableCards?: boolean;
    prevCareCompare?: string;
}

export const PreventiveCare: React.FC<PreventiveCareProps> = props => {
    const {
        activePet,
        preventiveOptions,
        preventiveConfig,
        underwriter,
        wellnessValue,
        handleChange,
        showPlatinumRecommendation,
        showNumbered,
        showClickableCards,
        prevCareCompare
    } = props;
    // test start ENG-18988 prevCareCompare
    // D1 — the variants replace the card carousel wholesale with a side-by-side table. Control's
    // render path below is untouched, so teardown is deleting this branch.
    const compareVariant = isPrevCareCompareVariant(prevCareCompare) ? prevCareCompare : undefined;
    // test end
    const breedMeta = IconUtils.getBreedPreventiveMeta(activePet);

    // Render nothing if there are no options or required data is missing
    if (!preventiveOptions || preventiveOptions.length <= 1 || !activePet || !underwriter) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex flex-col gap-4",
                showNumbered && "rounded-3xl border border-stroke-primary bg-background-primary p-3 sm:p-6",
                compareVariant && "gap-8 pr-0 sm:pr-3 lg:pr-6"
            )}
        >
            <PreventiveCareHeader showNumbered={showNumbered} inlineChipsFrom={compareVariant ? "lg" : "md"} />
            {/* test start ENG-18988 prevCareCompare — teardown: drop the ternary, keep <PreventiveCards /> */}
            {compareVariant ? (
                <PreventiveCompareTable
                    pet={activePet}
                    preventiveOptions={preventiveOptions}
                    preventiveConfig={preventiveConfig}
                    underwriter={underwriter}
                    wellnessLevel={wellnessValue}
                    handleChange={handleChange}
                    variant={compareVariant}
                />
            ) : (
                /* test end */
                <PreventiveCards
                    pet={activePet}
                    preventiveOptions={preventiveOptions}
                    preventiveConfig={preventiveConfig}
                    underwriter={underwriter}
                    wellnessLevel={wellnessValue}
                    handleChange={handleChange}
                    showNumbered={showNumbered}
                    showPlatinumRecommendation={showPlatinumRecommendation}
                    showClickableCards={showClickableCards}
                />
            )}
            {/* Only the cards carry the numbered breed badge this note footnotes. The compare table does not,
                and since the recs stopped being a layer arm the layer no longer keeps a visitor out of both. */}
            {breedMeta && !compareVariant && (
                <div data-testid="preventive-rec-disclaimers" className="text-center text-xs font-medium leading-4 text-content-secondary">
                    <p>
                        <sup>2</sup> {breedMeta.disclaimer}
                    </p>
                    <p className="mt-3">
                        <span className="font-bold">Source:</span>{" "}
                        {breedMeta.sources.map((s, i) => (
                            <span key={s.url}>
                                <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline">
                                    {s.label}
                                </a>
                                {i < breedMeta.sources.length - 1 && ", "}
                            </span>
                        ))}
                    </p>
                </div>
            )}
        </div>
    );
};
